/**
 * RecepcionTablaDensa — Vista agrupada por pasadía para operación de muelle.
 *
 * Responsive:
 *   - Desktop (md+): tabla densa con columnas, headers sticky, footer de grupo.
 *   - Mobile (xs/sm): cards apiladas dentro de cada grupo, header de grupo sticky,
 *     toda la info esencial visible en una tarjeta (sin abrir SlideOver para datos básicos).
 *
 * Ambas vistas:
 *   - Pasadías ordenados alfabéticamente A→Z (no por hora).
 *   - Reservas dentro de cada grupo ya vienen ordenadas por nombre desde el backend.
 *   - Status visual con bullet de color.
 *   - Badges para cortesías, descuentos, walk-in.
 *   - Pagos parciales condensados con popover.
 *
 * Optimización latencia móvil:
 *   - Render memoizado por grupo (cambios en un grupo no re-renderizan los demás).
 *   - Sin imágenes ni assets pesados.
 *   - Cards compactas, tap target ≥48px.
 *   - El collapse por grupo (mobile) permite enfocar un pasadía a la vez.
 */
import React, { useState, useMemo, memo } from 'react';
import {
  Box, Typography, Chip, Tooltip, Popover, Paper, Stack, Collapse, IconButton,
  useMediaQuery, useTheme,
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatNombre } from '../../utils/formatNombre';

function fmtMoney(n, m = 'COP') {
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: m, maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `$${n}`; }
}

function norm(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// Lo que se le cobra al pasajero en el embarcadero.
//
// El backend ya lo resuelve y lo entrega en `saldo_a_cobrar_muelle`: con saldo
// repartido es la parte del muelle, y sin repartir cae al saldo completo. Esta
// pantalla usaba `saldo_pendiente` a secas, así que en una reserva mixta le
// pedía al cajero cobrar también la parte que asume la agencia — en
// ENT-20260908-010 eran $480.000 en vez de $420.000.
function aCobrarEnMuelle(r) {
  // Sin division declarada, una reserva que asume la agencia no deja nada que
  // cobrar aqui. Se comprueba antes de mirar el campo del backend para que la
  // pantalla siga bien aunque ese campo cambie.
  const hayDivision = r.saldo_muelle_monto != null && r.cxc_agencia_monto != null;
  if (!hayDivision && r.saldo_a_cargo_de === 'agencia') return 0;
  if (r.saldo_a_cobrar_muelle !== undefined && r.saldo_a_cobrar_muelle !== null) {
    return Number(r.saldo_a_cobrar_muelle || 0);
  }
  if (r.saldo_muelle_monto !== undefined && r.saldo_muelle_monto !== null) {
    return Number(r.saldo_muelle_monto || 0);
  }
  if (r.saldo_a_cargo_de === 'agencia') return 0;
  return Number(r.saldo_pendiente || 0);
}

// Lo que queda contra el cupo de la agencia y NO se cobra aquí: se liquida
// después desde Finanzas.
function cxcDeAgencia(r) {
  if (r.cxc_agencia_monto !== undefined && r.cxc_agencia_monto !== null) {
    return Number(r.cxc_agencia_monto || 0);
  }
  return r.saldo_a_cargo_de === 'agencia' ? Number(r.saldo_pendiente || 0) : 0;
}

// Regla: si saldo>0 pero saldo_a_cargo_de='agencia' → CxC. La agencia asume
// contra su cupo; NO se cobra en muelle. Se liquida después desde Finanzas.
// Con saldo repartido NO es «CxC pura»: hay que cobrar la parte del muelle.
function esCxCAgencia(r) {
  return cxcDeAgencia(r) > 0 && aCobrarEnMuelle(r) <= 0;
}

function statusBullet(r) {
  if (r.estado_embarque === 'embarcado') return { color: '#16a34a', label: 'Embarcado' };
  if (r.estado_embarque === 'no_show') return { color: '#a855f7', label: 'No-show' };
  if (esCxCAgencia(r)) return { color: '#0891b2', label: `CxC ${fmtMoney(cxcDeAgencia(r))}` };
  if (aCobrarEnMuelle(r) > 0) return { color: '#dc2626', label: `Debe ${fmtMoney(aCobrarEnMuelle(r))}` };
  if (r.estado_embarque === 'cobrado') return { color: '#0d9488', label: 'Cobrado · por embarcar' };
  return { color: '#94a3b8', label: 'Pendiente' };
}

function fmtPaxBadge(r) {
  const parts = [];
  if (r.adultos) parts.push(`${r.adultos}A`);
  if (r.ninos) parts.push(`${r.ninos}N`);
  if (r.bebes) parts.push(`${r.bebes}B`);
  return parts.join(' · ') || '—';
}

export default function RecepcionTablaDensa({
  pasadias = [],
  search = '',
  estadoFiltro = 'todas',
  pasadiaFiltro = 'todas',
  onSelectReserva,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  // En desktop pero por debajo de xl (< 1536) la tabla está apretada: a
  // 1366×768 las columnas Código y Pax no caben sin scroll horizontal porque
  // el menú lateral come ~240px. Por debajo de xl ocultamos Código + Pax y
  // reducimos el padding; a partir de xl se ven todas las columnas.
  const isCompactDesktop = isDesktop && !isXl;
  const [pagosAnchor, setPagosAnchor] = useState(null);

  // Pasadías ordenados A→Z
  const pasadiasOrdenadas = useMemo(() => {
    const filtered = pasadiaFiltro === 'todas'
      ? [...pasadias]
      : pasadias.filter(p => String(p.salida_id) === String(pasadiaFiltro));
    return filtered.sort((a, b) =>
      norm(a.paquete_nombre).localeCompare(norm(b.paquete_nombre))
    );
  }, [pasadias, pasadiaFiltro]);

  const aplicarFiltrosReserva = (r) => {
    // "Pendiente de pago" excluye CxC (la agencia asume el saldo — no se cobra en muelle)
    if (estadoFiltro === 'pendiente_pago' && !(aCobrarEnMuelle(r) > 0)) return false;
    if (estadoFiltro === 'sin_checkin' && r.estado_embarque === 'embarcado') return false;
    if (estadoFiltro === 'embarcados' && r.estado_embarque !== 'embarcado') return false;
    if (estadoFiltro === 'no_show' && r.estado_embarque !== 'no_show') return false;
    const q = norm(search.trim());
    if (q) {
      const hay = norm(`${r.codigo_reserva} ${r.nombre_cliente} ${r.telefono || ''} ${r.agencia_nombre || ''}`);
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const totalReservasVisibles = pasadiasOrdenadas
    .reduce((acc, p) => acc + p.reservas.filter(aplicarFiltrosReserva).length, 0);

  if (totalReservasVisibles === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>
          Sin reservas que coincidan con los filtros.
        </Typography>
      </Paper>
    );
  }

  if (isDesktop) {
    return (
      <DesktopTabla
        pasadias={pasadiasOrdenadas}
        aplicarFiltrosReserva={aplicarFiltrosReserva}
        onSelectReserva={onSelectReserva}
        pagosAnchor={pagosAnchor}
        setPagosAnchor={setPagosAnchor}
        compact={isCompactDesktop}
      />
    );
  }

  return (
    <MobileAgrupado
      pasadias={pasadiasOrdenadas}
      aplicarFiltrosReserva={aplicarFiltrosReserva}
      onSelectReserva={onSelectReserva}
      pagosAnchor={pagosAnchor}
      setPagosAnchor={setPagosAnchor}
    />
  );
}

// ============================================================================
// DESKTOP — Tabla densa con headers sticky
// ============================================================================

function DesktopTabla({ pasadias, aplicarFiltrosReserva, onSelectReserva, pagosAnchor, setPagosAnchor, compact = false }) {
  // En modo compacto (1366 y similares) bajamos padding horizontal de 10 → 6
  // y ocultamos columnas Pax y Código (las menos consultadas durante embarque).
  // El operador ve Pax y Código en el popover de la fila si lo necesita.
  const pad = compact ? '4px 6px' : '6px 10px';
  const padHead = compact ? '6px 6px' : '8px 10px';
  const colCount = compact ? 8 : 10;
  return (
    <Box sx={{
      borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden',
      bgcolor: '#fff',
    }}>
      <Box sx={{
        maxHeight: 'calc(100vh - 280px)', overflow: 'auto',
        '& thead th': {
          position: 'sticky', top: 0, zIndex: 3, bgcolor: '#0f172a', color: '#fff',
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
          padding: padHead, textAlign: 'left', whiteSpace: 'nowrap',
        },
        '& thead th.num': { textAlign: 'right' },
        '& tbody td': {
          fontSize: 12, padding: pad, borderBottom: '1px solid #f1f5f9',
          verticalAlign: 'middle',
        },
        '& tbody td.num': { textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 },
        '& tbody tr:hover': { bgcolor: '#f0fdfa', cursor: 'pointer' },
        '& tr.grupo-header td': {
          position: 'sticky', top: 33, zIndex: 2,
          bgcolor: '#0d9488', color: '#fff',
          fontSize: 12, fontWeight: 800, padding: pad, letterSpacing: 0.3,
        },
        '& tr.grupo-footer td': {
          bgcolor: '#f1f5f9', fontSize: 11, fontWeight: 700, color: '#475569',
          padding: compact ? '3px 6px' : '4px 10px',
        },
      }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ width: 12 }}></th>
              <th>Nombre cliente</th>
              {!compact && <th className="num">Pax</th>}
              {!compact && <th>Código</th>}
              <th>Agencia</th>
              <th>Embarcación</th>
              <th className="num">Venta neta</th>
              <th className="num">Por cobrar</th>
              <th className="num">Pagos</th>
              <th>Hora</th>
            </tr>
          </thead>
          <tbody>
            {pasadias.map(p => (
              <GrupoDesktop key={p.salida_id} pasadia={p}
                aplicarFiltrosReserva={aplicarFiltrosReserva}
                onSelectReserva={onSelectReserva}
                setPagosAnchor={setPagosAnchor}
                compact={compact}
                colCount={colCount} />
            ))}
          </tbody>
        </Box>
      </Box>
      <PagosPopover pagosAnchor={pagosAnchor} setPagosAnchor={setPagosAnchor} />
    </Box>
  );
}

const GrupoDesktop = memo(function GrupoDesktop({ pasadia: p, aplicarFiltrosReserva, onSelectReserva, setPagosAnchor, compact = false, colCount = 10 }) {
  const reservasVisibles = p.reservas.filter(aplicarFiltrosReserva);
  if (reservasVisibles.length === 0) return null;
  const subtotalPagantes = reservasVisibles.reduce((acc, r) => acc + (r.total_pax || 0), 0);
  // Subtotal de "deuda" del muelle EXCLUYE CxC (esas van al dashboard Finanzas → CxC).
  const subtotalDeuda = reservasVisibles.reduce((acc, r) => (
    acc + aCobrarEnMuelle(r)
  ), 0);
  const subtotalNeto = reservasVisibles.reduce((acc, r) => acc + Number(r.precio_total || 0), 0);
  return (
    <>
      <tr className="grupo-header">
        <td colSpan={colCount}>
          🏝️ {p.paquete_nombre}
          {p.hora_salida && <span style={{ opacity: 0.85, fontWeight: 600 }}> · {String(p.hora_salida).slice(0, 5)}</span>}
          <span style={{ marginLeft: 12, opacity: 0.85, fontWeight: 600 }}>
            {reservasVisibles.length} reserva{reservasVisibles.length !== 1 ? 's' : ''} · {subtotalPagantes} pax
          </span>
        </td>
      </tr>
      {reservasVisibles.map(r => (
        <FilaDesktop key={r.id} r={r} salida={p}
          onSelectReserva={onSelectReserva}
          setPagosAnchor={setPagosAnchor}
          compact={compact} />
      ))}
      <tr className="grupo-footer">
        <td></td>
        <td><Typography sx={{ fontSize: 10, fontWeight: 700, color: '#475569',
                                 textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Subtotal {p.paquete_nombre}
        </Typography></td>
        {!compact && <td className="num">{subtotalPagantes} pax</td>}
        {!compact && <td></td>}
        <td></td><td></td>
        <td className="num" style={{ color: '#0f172a' }}>{fmtMoney(subtotalNeto)}</td>
        <td className="num" style={{ color: subtotalDeuda > 0 ? '#dc2626' : '#16a34a' }}>
          {subtotalDeuda > 0 ? fmtMoney(subtotalDeuda) : 'OK'}
        </td>
        <td></td><td></td>
      </tr>
    </>
  );
});

const FilaDesktop = memo(function FilaDesktop({ r, salida, onSelectReserva, setPagosAnchor, compact = false }) {
  const st = statusBullet(r);
  const tieneCortesia = (r.cortesias_total || 0) > 0;
  const tieneDescuento = (r.descuento_comision_agencia || 0) > 0 || (r.descuento_comercial || 0) > 0;
  const descTotal = (r.descuento_comision_agencia || 0) + (r.descuento_comercial || 0);
  // En compact embebemos el código en el tooltip del nombre y subtítulo,
  // y limitamos agencia un poco más para ganar ancho.
  return (
    <tr onClick={() => onSelectReserva?.({ ...r, _salida: salida })}>
      <td>
        <Tooltip title={st.label} placement="right">
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, mx: 'auto' }} />
        </Tooltip>
      </td>
      <td>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
          <Tooltip title={compact ? `${r.codigo_reserva} · ${fmtPaxBadge(r)} pax` : ''}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
              {formatNombre(r.nombre_cliente)}
            </Typography>
          </Tooltip>
          {compact && (
            <Typography sx={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>
              {fmtPaxBadge(r)} · {r.codigo_reserva.slice(-6)}
            </Typography>
          )}
          {tieneCortesia && (
            <Tooltip title={`🎁 Cortesía: ${r.cortesias_adultos || 0}A · ${r.cortesias_ninos || 0}N${r.cortesias_motivo ? ' · ' + r.cortesias_motivo : ''}${r.cortesias_autorizada_por_nombre ? ' · Autorizada: ' + r.cortesias_autorizada_por_nombre : ''}`}>
              <Chip size="small" icon={<CardGiftcardIcon sx={{ fontSize: 11 }} />}
                label={`${r.cortesias_total} cortesía${r.cortesias_total > 1 ? 's' : ''}`}
                sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
            </Tooltip>
          )}
          {tieneDescuento && (
            <Tooltip title={`Descuento aplicado: ${fmtMoney(descTotal)}`}>
              <Chip size="small" icon={<LocalOfferIcon sx={{ fontSize: 11 }} />}
                label={`−${fmtMoney(descTotal)}`}
                sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#f3e8ff', color: '#7e22ce' }} />
            </Tooltip>
          )}
          {r.es_walkin && (
            <Chip size="small" label="Walk-in"
              sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#dbeafe', color: '#1e40af' }} />
          )}
        </Stack>
      </td>
      {!compact && <td className="num">{fmtPaxBadge(r)}</td>}
      {!compact && (
        <td>
          <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: '#475569' }}>
            {r.codigo_reserva}
          </Typography>
        </td>
      )}
      <td>
        <Typography sx={{ fontSize: 11.5, color: '#475569',
                            maxWidth: compact ? 120 : 160, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.agencia_nombre || '—'}
        </Typography>
      </td>
      <td>
        {r.embarcacion_asignada_nombre ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <DirectionsBoatIcon sx={{ fontSize: 12, color: '#0d9488' }} />
            <Typography sx={{ fontSize: 11.5 }}>{r.embarcacion_asignada_nombre}</Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
        )}
      </td>
      <td className="num" style={{ color: '#0f172a' }}>{fmtMoney(r.precio_total)}</td>
      <td className="num" style={{
        color: esCxCAgencia(r) ? '#0891b2'
             : aCobrarEnMuelle(r) > 0 ? '#dc2626'
             : '#16a34a',
      }}>
        {esCxCAgencia(r) ? `CxC ${fmtMoney(cxcDeAgencia(r))}`
         : aCobrarEnMuelle(r) > 0 ? fmtMoney(aCobrarEnMuelle(r))
         : 'OK'}
      </td>
      <td className="num">
        {(r.pagos || []).length === 0 ? (
          <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
        ) : (
          <Chip size="small" icon={<ReceiptLongIcon sx={{ fontSize: 12 }} />}
            label={`${r.pagos.length} · ${fmtMoney(r.total_pagado)}`}
            onClick={(e) => { e.stopPropagation(); setPagosAnchor({ el: e.currentTarget, pagos: r.pagos, codigo: r.codigo_reserva }); }}
            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#dcfce7', color: '#166534', cursor: 'pointer',
                    '&:hover': { bgcolor: '#bbf7d0' } }} />
        )}
      </td>
      <td>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#0d9488' }}>
          {salida.hora_salida ? String(salida.hora_salida).slice(0, 5) : '—'}
        </Typography>
      </td>
    </tr>
  );
});

// ============================================================================
// MOBILE — Cards apiladas agrupadas por pasadía, header de grupo sticky
// ============================================================================

function MobileAgrupado({ pasadias, aplicarFiltrosReserva, onSelectReserva, pagosAnchor, setPagosAnchor }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {pasadias.map(p => (
        <GrupoMobile key={p.salida_id} pasadia={p}
          aplicarFiltrosReserva={aplicarFiltrosReserva}
          onSelectReserva={onSelectReserva}
          setPagosAnchor={setPagosAnchor} />
      ))}
      <PagosPopover pagosAnchor={pagosAnchor} setPagosAnchor={setPagosAnchor} />
    </Box>
  );
}

const GrupoMobile = memo(function GrupoMobile({ pasadia: p, aplicarFiltrosReserva, onSelectReserva, setPagosAnchor }) {
  const [colapsado, setColapsado] = useState(false);
  const reservasVisibles = p.reservas.filter(aplicarFiltrosReserva);
  if (reservasVisibles.length === 0) return null;
  const subtotalPagantes = reservasVisibles.reduce((acc, r) => acc + (r.total_pax || 0), 0);
  // Subtotal de "deuda" del muelle EXCLUYE CxC (esas van al dashboard Finanzas → CxC).
  const subtotalDeuda = reservasVisibles.reduce((acc, r) => (
    acc + aCobrarEnMuelle(r)
  ), 0);

  return (
    <Box>
      {/* Header sticky por grupo */}
      <Paper onClick={() => setColapsado(c => !c)}
        sx={{
          position: 'sticky', top: 0, zIndex: 5,
          bgcolor: '#0d9488', color: '#fff', borderRadius: 1.5,
          px: 1.25, py: 0.85, display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', mb: 1,
        }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.3 }}>
            🏝️ {p.paquete_nombre}
            {p.hora_salida && (
              <span style={{ opacity: 0.85, fontWeight: 700, marginLeft: 8 }}>
                · {String(p.hora_salida).slice(0, 5)}
              </span>
            )}
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.9 }}>
            {reservasVisibles.length} reserva{reservasVisibles.length !== 1 ? 's' : ''} · {subtotalPagantes} pax
            {subtotalDeuda > 0 && (
              <span style={{ marginLeft: 8 }}>
                · <b>Por cobrar {fmtMoney(subtotalDeuda)}</b>
              </span>
            )}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: '#fff', p: 0.5 }}>
          {colapsado ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Paper>

      <Collapse in={!colapsado} unmountOnExit>
        {/* Rejilla en vez de pila: en un teléfono queda una columna, y en una
            tablet —donde antes sobraba la mitad de la pantalla— caben dos o
            tres sin que la tarjeta se deforme. */}
        <Box sx={{ display: 'grid', gap: 1,
                   gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(300px, 1fr))' } }}>
          {reservasVisibles.map(r => (
            <CardMobile key={r.id} r={r} salida={p}
              onSelectReserva={onSelectReserva}
              setPagosAnchor={setPagosAnchor} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
});

const CardMobile = memo(function CardMobile({ r, salida, onSelectReserva, setPagosAnchor }) {
  const st = statusBullet(r);
  const tieneCortesia = (r.cortesias_total || 0) > 0;
  const tieneDescuento = (r.descuento_comision_agencia || 0) > 0 || (r.descuento_comercial || 0) > 0;
  const descTotal = (r.descuento_comision_agencia || 0) + (r.descuento_comercial || 0);
  return (
    <Paper
      onClick={() => onSelectReserva?.({ ...r, _salida: salida })}
      sx={{
        p: 1.25, borderRadius: 2,
        borderLeft: `4px solid ${st.color}`,
        cursor: 'pointer',
        minHeight: 58, display: 'flex', alignItems: 'center',
        '&:active': { transform: 'scale(0.98)' },
        transition: 'transform 0.1s',
      }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', mb: 0.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a',
                                  maxWidth: '100%', overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatNombre(r.nombre_cliente)}
            </Typography>
            {tieneCortesia && (
              <Chip size="small" icon={<CardGiftcardIcon sx={{ fontSize: 12 }} />}
                label={`${r.cortesias_total} cortesía${r.cortesias_total > 1 ? 's' : ''}`}
                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
            )}
            {tieneDescuento && (
              <Chip size="small" icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                label={`−${fmtMoney(descTotal)}`}
                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#f3e8ff', color: '#7e22ce' }} />
            )}
            {r.es_walkin && (
              <Chip size="small" label="Walk-in"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#dbeafe', color: '#1e40af' }} />
            )}
          </Stack>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.codigo_reserva}</span>
            {' · '}{fmtPaxBadge(r)}
            {r.agencia_nombre && ` · ${r.agencia_nombre}`}
          </Typography>
          {r.embarcacion_asignada_nombre && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
              <DirectionsBoatIcon sx={{ fontSize: 11, color: '#0d9488' }} />
              <Typography sx={{ fontSize: 10.5, color: '#0d9488', fontWeight: 600 }}>
                {r.embarcacion_asignada_nombre}
              </Typography>
            </Stack>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: { xs: 96, sm: 110 }, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800,
                              color: esCxCAgencia(r) ? '#0891b2'
                                   : aCobrarEnMuelle(r) > 0 ? '#dc2626'
                                   : '#16a34a' }}>
            {esCxCAgencia(r) ? `CxC ${fmtMoney(cxcDeAgencia(r))}`
             : aCobrarEnMuelle(r) > 0 ? fmtMoney(aCobrarEnMuelle(r))
             : 'OK'}
          </Typography>
          <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
            {esCxCAgencia(r) ? 'contra cupo agencia'
             : aCobrarEnMuelle(r) > 0
               ? (cxcDeAgencia(r) > 0
                 ? `por cobrar · ${fmtMoney(cxcDeAgencia(r))} van a CxC`
                 : 'por cobrar')
               : 'pagado'}
          </Typography>
          {(r.pagos || []).length > 0 && (
            <Chip size="small" icon={<ReceiptLongIcon sx={{ fontSize: 12 }} />}
              label={`${r.pagos.length} pago${r.pagos.length > 1 ? 's' : ''}`}
              onClick={(e) => { e.stopPropagation(); setPagosAnchor({ el: e.currentTarget, pagos: r.pagos, codigo: r.codigo_reserva }); }}
              sx={{ mt: 0.5, height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }} />
          )}
        </Box>
      </Stack>
    </Paper>
  );
});

// ============================================================================
// Popover compartido para desglose de pagos
// ============================================================================

function PagosPopover({ pagosAnchor, setPagosAnchor }) {
  return (
    <Popover open={!!pagosAnchor} anchorEl={pagosAnchor?.el}
      onClose={() => setPagosAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { p: 1.5, minWidth: 260, maxWidth: 320 } } }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b',
                          textTransform: 'uppercase', mb: 0.5 }}>
        Pagos de {pagosAnchor?.codigo}
      </Typography>
      <Stack spacing={0.5}>
        {(pagosAnchor?.pagos || []).map((p, idx) => (
          <Stack key={p.id} direction="row" alignItems="center" spacing={1}
            sx={{ borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none', pt: idx > 0 ? 0.5 : 0 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{fmtMoney(p.monto)}</Typography>
              <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                {p.origen}{p.metodo_pago ? ` · ${p.metodo_pago}` : ''}
                {p.created_at && ` · ${new Date(p.created_at).toLocaleDateString('es-CO')}`}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Popover>
  );
}
